import type { WorldDef } from '../content/campaign';
import type { LevelDef } from '../sim/defs';

/* ═══════════════════════════════════════════════════════════════════════════
   LES EXERCICES D'ENTRAÎNEMENT.

   ⚠️ Ce module existe pour être TESTABLE. La règle qu'il porte — on ne
   s'entraîne que sur ce qu'on a franchi — est exactement le genre de condition
   qu'on écrit une fois dans `main.ts`, qu'aucun test ne peut atteindre, et qui
   se met à mentir six semaines plus tard sans que rien ne l'indique.

   Le point d'entrée du jeu orchestre ; il ne décide pas. Une règle de jeu qui
   vit dans `main.ts` est une règle qu'on ne peut ni éprouver ni relire.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface Exercice {
  readonly id: string;
  readonly levelId: string;
  /** Index du bloc de départ dans la partition du niveau. */
  readonly fromBlock: number;
  readonly titre: string;
  readonly detail: string;
}

/** Les deux nœuds du hangar qui ouvrent l'entraînement. */
export const DRILL_SECTEURS = 'raccourci-secteurs';
export const DRILL_BOSS = 'salle-entrainement';

/** L'écran est-il atteignable ? La liste peut être vide pour autant — un joueur
 *  peut acheter l'accès avant d'avoir franchi quoi que ce soit, et l'écran le
 *  lui dit plutôt que de lui montrer une page blanche. */
export function trainingUnlocked(unlocked: ReadonlySet<string>): boolean {
  return unlocked.has(DRILL_SECTEURS) || unlocked.has(DRILL_BOSS);
}

/**
 * Exercices ouverts, déduits des JALONS franchis.
 *
 * ⚠️ Aucun champ de sauvegarde dédié : « monde N franchi » est déjà un jalon de
 * maîtrise. Le dupliquer créerait deux sources de vérité pour la même question,
 * et elles finiraient par diverger — c'est toujours celle qu'on oublie de
 * mettre à jour qui pilote l'écran.
 *
 * ⚠️ « Déjà franchi » n'est pas une formalité. Un boss répétable sans l'avoir
 * jamais atteint ferait de la salle un raccourci de CONTENU : elle doit
 * supprimer le trajet, jamais l'épreuve.
 */
export function drillsFor(
  worlds: readonly WorldDef[],
  levels: ReadonlyMap<string, LevelDef>,
  unlocked: ReadonlySet<string>,
  milestones: ReadonlySet<string>,
): Exercice[] {
  const out: Exercice[] = [];

  for (let i = 0; i < worlds.length; i++) {
    const world = worlds[i]!;
    if (!milestones.has(`monde-${i + 1}-franchi`)) continue;
    // Le boss vit dans le DERNIER niveau du monde — c'est lui qu'on rejoue, que
    // ce soit « le niveau final en entier » ou « le boss seul ».
    const bossLevelId = world.levels[world.levels.length - 1];
    if (!bossLevelId) continue;
    const level = levels.get(bossLevelId);
    if (!level) continue;

    if (unlocked.has(DRILL_SECTEURS)) {
      out.push({
        id: `${bossLevelId}-complet`,
        levelId: bossLevelId,
        fromBlock: 0,
        titre: `Monde ${i + 1} — niveau final`,
        detail: 'Le dernier niveau du secteur, boss compris.',
      });
    }
    if (unlocked.has(DRILL_BOSS)) {
      // Le bloc de boss se reconnaît à la convention de nommage `boss-`, celle
      // dont `levelSystem` se sert déjà pour basculer en attente de mise à mort.
      // Une seconde façon de désigner un boss finirait par en désigner un autre.
      const bloc = level.blocks.findIndex((b) => b.wave.startsWith('boss-'));
      if (bloc >= 0) {
        out.push({
          id: `${bossLevelId}-boss`,
          levelId: bossLevelId,
          fromBlock: bloc,
          titre: `Monde ${i + 1} — boss seul`,
          detail: 'Le combat, sans les minutes qui le précèdent.',
        });
      }
    }
  }
  return out;
}
